import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AppShell } from '../components/AppShell';
import { Avatar } from '../components/Avatar';
import { useFriends } from '../hooks/useFriends';
import { useSession } from '../hooks/useSession';
import { api } from '../lib/api';
import { getSocket } from '../lib/socket';
import { useActiveGame } from '../hooks/useActiveGame';

export default function FriendsPage() {
  const user = useSession(s => s.user);
  const isGuest = !!user?.isGuest;
  const { friends, incoming, outgoing, loading, refresh, attach } = useFriends();
  const refreshActive = useActiveGame(s => s.refresh);
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; name: string; avatar: string | null }>>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (isGuest) return;
    refresh();
    return attach();
  }, [isGuest, refresh, attach]);

  useEffect(() => {
    if (isGuest) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setSearchResults([]); return; }
      try {
        const r = await api.searchUsers(q.trim());
        // A slower, older response must not overwrite a newer query's results.
        if (!cancelled) setSearchResults(r);
      } catch { /* ignore */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, isGuest]);

  async function sendRequest(toId: string) {
    setBusy(toId); setErr(null);
    try {
      await api.sendFriendRequest(toId);
      await refresh();
      setQ('');
      setSearchResults([]);
    } catch (e: any) {
      setErr(e.message || 'Could not send request');
    } finally {
      setBusy(null);
    }
  }
  async function respond(requestId: string, accept: boolean) {
    setBusy(requestId); setErr(null);
    try {
      await api.respondFriendRequest(requestId, accept);
      await refresh();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  }
  async function cancel(requestId: string) {
    setBusy(requestId);
    try { await api.cancelFriendRequest(requestId); await refresh(); }
    finally { setBusy(null); }
  }
  async function unfriend(userId: string) {
    if (!confirm('Remove this friend?')) return;
    setBusy(userId);
    try { await api.removeFriend(userId); await refresh(); }
    finally { setBusy(null); }
  }
  async function inviteToNewGame(friendId: string) {
    setBusy(friendId);
    const sock = getSocket();
    sock.emit('game:create', { isPrivate: true, maxPlayers: 4 }, (res) => {
      if (!res.ok) { setErr(res.error); setBusy(null); return; }
      const gameId = res.data.gameId;
      sock.emit('push:invite', { gameId, toUserId: friendId }, () => {
        refreshActive();
        nav(`/game/${gameId}`);
      });
    });
  }

  if (isGuest) {
    return (
      <AppShell>
        <div className="panel p-8 text-center space-y-3">
          <div className="text-4xl">👥</div>
          <h2 className="font-display text-lg font-bold">Friends are for signed-in players</h2>
          <p className="text-ink-200 text-sm max-w-md mx-auto">
            Guest sessions are anonymous. Sign in with Google to add friends, track head-to-head records, and invite them with one tap.
          </p>
          <a href="/auth/google" className="btn-primary inline-flex">Continue with Google</a>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <h1 className="font-display text-2xl font-bold mb-4">Friends</h1>
      {err && <div className="mb-3 text-rose-400 text-sm">{err}</div>}

      {/* Search & add */}
      <section className="panel p-4 mb-4">
        <h2 className="section-label mb-2">Add a friend</h2>
        <input
          className="input"
          placeholder="Search by name (min 2 characters)"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        {searchResults.length > 0 && (
          <ul className="mt-2 divide-y divide-ink-700/60 max-h-60 overflow-y-auto">
            {searchResults
              .filter(u => u.id !== user!.id)
              .map(u => {
                const isFriend = friends.some(f => f.id === u.id);
                const sentReq = outgoing.find(r => r.to?.id === u.id);
                const incReq = incoming.find(r => r.from?.id === u.id);
                return (
                  <li key={u.id} className="flex items-center gap-2 py-2">
                    <Avatar src={u.avatar} name={u.name} />
                    <span className="flex-1 min-w-0 truncate text-sm">{u.name}</span>
                    {isFriend ? (
                      <span className="chip">Friend</span>
                    ) : sentReq ? (
                      <span className="chip">Pending</span>
                    ) : incReq ? (
                      <button
                        className="btn-primary !py-1 !px-3 text-xs"
                        disabled={busy === incReq.id}
                        onClick={() => respond(incReq.id, true)}
                      >Accept</button>
                    ) : (
                      <button
                        className="btn-secondary !py-1 !px-3 text-xs"
                        disabled={busy === u.id}
                        onClick={() => sendRequest(u.id)}
                      >Add</button>
                    )}
                  </li>
                );
              })}
          </ul>
        )}
      </section>

      {/* Incoming requests */}
      {incoming.length > 0 && (
        <section className="panel p-4 mb-4">
          <h2 className="section-label mb-2">Incoming requests</h2>
          <ul className="divide-y divide-ink-700/60">
            {incoming.map(r => r.from && (
              <li key={r.id} className="flex items-center gap-2 py-2">
                <Avatar src={r.from.avatar} name={r.from.name} />
                <span className="flex-1 min-w-0 truncate text-sm">{r.from.name}</span>
                <button className="btn-primary !py-1 !px-3 text-xs" disabled={busy === r.id} onClick={() => respond(r.id, true)}>Accept</button>
                <button className="btn-ghost !py-1 !px-3 text-xs" disabled={busy === r.id} onClick={() => respond(r.id, false)}>Decline</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Outgoing */}
      {outgoing.length > 0 && (
        <section className="panel p-4 mb-4">
          <h2 className="section-label mb-2">Sent — waiting</h2>
          <ul className="divide-y divide-ink-700/60">
            {outgoing.map(r => r.to && (
              <li key={r.id} className="flex items-center gap-2 py-2">
                <Avatar src={r.to.avatar} name={r.to.name} />
                <span className="flex-1 min-w-0 truncate text-sm">{r.to.name}</span>
                <button className="btn-ghost !py-1 !px-3 text-xs" disabled={busy === r.id} onClick={() => cancel(r.id)}>Cancel</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Friends list */}
      <section className="panel p-4">
        <h2 className="section-label mb-2">My friends ({friends.length})</h2>
        {friends.length === 0 ? (
          <div className="text-ink-300 text-sm italic">{loading ? 'Loading…' : 'No friends yet — search above to send a request.'}</div>
        ) : (
          <ul className="divide-y divide-ink-700/60">
            {friends.map(f => (
              <li key={f.id} className="flex items-center gap-2 py-2">
                <div className="relative">
                  <Avatar src={f.avatar} name={f.name} />
                  <span
                    className={clsx(
                      'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-ink-800',
                      f.online ? 'bg-emerald-400' : 'bg-zinc-500',
                    )}
                  />
                </div>
                <Link to={`/players/${f.id}`} className="flex-1 min-w-0 hover:bg-ink-700/40 rounded px-1 -mx-1">
                  <div className="text-sm font-semibold truncate">{f.name}</div>
                  <div className="text-[11px] text-ink-200/80 flex gap-2">
                    <span className={f.online ? 'text-emerald-400' : ''}>{f.online ? 'online' : 'offline'}</span>
                    {f.h2h.gamesPlayed > 0 && (
                      <span>
                        H2H{' '}
                        <span className="text-emerald-400">{f.h2h.wins}W</span>
                        {' / '}
                        <span className="text-rose-400">{f.h2h.losses}L</span>
                        {f.h2h.draws > 0 && <> / <span>{f.h2h.draws}D</span></>}
                      </span>
                    )}
                  </div>
                </Link>
                <button
                  className="btn-primary !py-1 !px-3 text-xs"
                  disabled={busy === f.id}
                  onClick={() => inviteToNewGame(f.id)}
                  title="Create a private room and notify this friend"
                >Invite</button>
                <button
                  className="btn-ghost !py-1 !px-2 text-xs text-ink-300"
                  disabled={busy === f.id}
                  onClick={() => unfriend(f.id)}
                  aria-label="Remove"
                >✕</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </AppShell>
  );
}

